import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastService } from '@teorema/patron';
import { Subscription } from 'rxjs';
import {
  FilterField,
  FilterOperator,
  MovementHistory,
  Ticket,
  TicketDetail,
  TicketLog,
  User,
} from 'src/app/models';
import { MovementHistoryService, TcDataService, TcTicketService } from 'src/app/shared/services';
import { isWebDetailOperation } from 'src/app/shared/utils';

@Component({
  selector: 'ts-info-tab',
  templateUrl: './ts-info-tab.component.html',
  styleUrls: ['./ts-info-tab.component.scss'],
  standalone: false,
})
export class TsInfoTabComponent implements OnInit, OnDestroy {
  @Input() isSaving?: boolean;
  @Input() unconfirmedTicket?: Ticket;
  @Input() ticketLogs?: TicketLog[];
  @Input() user?: User;

  @Output() saving = new EventEmitter<boolean>();

  newDetail?: TicketDetail;
  timeline: any[] = [];
  subscriptions: Subscription[] = [];
  ticketMovementHistories: MovementHistory[] = [];
  formGroup!: FormGroup;
  loadingMovimentHistories: boolean = false;
  imageModalVisible = false;
  selectedImage?: string | null;
  detailsViewMode: 'markdown' | 'text' = 'markdown';
  private readonly detailViewModeStorageKey = 'tsInfoDetailViewMode';
  private readonly infoTextareaBaseRows = 1;
  private readonly infoTextareaFocusedRows = this.infoTextareaBaseRows + 4;
  infoTextareaRows = this.infoTextareaBaseRows;

  constructor(
    private movementHistoryService: MovementHistoryService,
    public ticketService: TcTicketService,
    private dataService: TcDataService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    this.formGroup = this.formBuilder.group({
      observation: [''], // Removido Validators.required - validação manual no isFormValid()
      isPublic: [false],
    });
  }

  ngOnInit(): void {
    this.loadDetailsViewMode();
    this.createNewDetail();
    this.subscriptions.push(
      this.dataService.sendDetailEmitter.subscribe($event => {
        this.newDetail!.observation = $event.observation!;
        this.newDetail!.pub = $event.pub;
        this._sendDetail();
      })
    );
  }

  createNewDetail() {
    let isPublic = false;
    if (this.newDetail) {
      isPublic = this.newDetail.pub!;
    }

    this.newDetail = new TicketDetail();
    this.newDetail.observation = '';
    this.newDetail.pub = isPublic;

    if (this.newDetail.pub != true && this.newDetail.pub != false) {
      this.newDetail.pub = true;
    }
  }

  openImageModal(image: string) {
    this.selectedImage = image;
    this.imageModalVisible = true;
  }

  closeImageModal() {
    this.imageModalVisible = false;
    this.selectedImage = null;
  }

  setDetailsViewMode(mode: 'markdown' | 'text') {
    if (this.detailsViewMode === mode) {
      return;
    }

    this.detailsViewMode = mode;
    this.persistDetailsViewMode();
  }

  get showDetailsAsMarkdown(): boolean {
    return this.detailsViewMode === 'markdown';
  }

  private loadDetailsViewMode(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const storedMode = window.localStorage.getItem(this.detailViewModeStorageKey) as
      | 'markdown'
      | 'text'
      | null;

    if (storedMode === 'markdown' || storedMode === 'text') {
      this.detailsViewMode = storedMode;
    }
  }

  private persistDetailsViewMode(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.detailViewModeStorageKey, this.detailsViewMode);
  }

  setDetailImage(detail: any): void {
    if (detail.observation && !detail.images) {
      const imageLinks = detail.observation.match(/https?:\/\/\S+\.(?:jpg|jpeg|png|gif)/g);
      if (imageLinks) {
        detail.images = imageLinks;
      }
    }
  }

  loadTimeline() {
    this.timeline = [];

    this.unconfirmedTicket!.details!.forEach(detail => {
      this.setDetailImage(detail);
      if (detail.observation && detail.observation.length) {
        this.timeline.push({
          type: 'D',
          pub: detail.pub,
          created_at: detail.created_at,
          user: detail.user,
          description: detail.observation,
          images: detail['images'] ?? [],
        });
      }

      // Código de máquina ('comment', 'movement', 'notation') não é texto para exibir: o que o
      // usuário lê já entrou acima como a observação. Só movimentação do HelpDesk, que escreve a
      // frase inteira em `operation`, vira um card próprio.
      if (detail.operation && detail.operation.length && !isWebDetailOperation(detail.operation)) {
        this.timeline.push({
          type: 'DMH',
          created_at: detail.created_at,
          user: detail.user,
          description: detail.operation,
        });
      }
    });

    this.ticketLogs!.forEach(log => {
      this.timeline.push({
        type: 'L',
        created_at: log.created_at,
        user: log.user,
        description: log.description,
        startDate: log.start_date,
        endDate: log.end_date,
      });
    });

    this.ticketMovementHistories.forEach(movementHistory => {
      if (movementHistory.description && movementHistory.description.length) {
        this.timeline.push({
          type: 'MH',
          created_at: movementHistory.created_at,
          user: movementHistory.user,
          description: movementHistory.description,
        });
      }
    });

    this.timeline.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }

  async loadTicketMovementHistories() {
    this.loadingMovimentHistories = true;
    let response = await this.movementHistoryService
      ?.findAll({
        filters: [
          new FilterField({
            name: 'table_name',
            label: 'Table',
            value: 'tickets',
            type: 'text',
            operator: FilterOperator.equal,
          }),
          new FilterField({
            name: 'uuid_origin',
            label: 'Origin',
            value: this.unconfirmedTicket!.id,
            type: 'text',
            operator: FilterOperator.equal,
          }),
        ],
      })
      .catch(() => {
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao carregar os dados',
        });
      });

    if (response?.data) {
      this.ticketMovementHistories = response.data;
    }

    this.loadTimeline();
    this.loadingMovimentHistories = false;
    this.cdr.markForCheck();
  }

  isFormValid() {
    // Validação do campo observation
    const observationControl = this.formGroup.get('observation');
    const observationValue = observationControl?.value || '';
    const isObservationValid = observationValue.trim().length > 0;

    // Validação do campo isPublic
    const isPublicControl = this.formGroup.get('isPublic');
    const isPublicValid = isPublicControl?.value !== null && isPublicControl?.value !== undefined;

    return isObservationValid && isPublicValid;
  }

  sendDetail() {
    const isValid = this.isFormValid();

    if (!isValid) {
      this.toastService.show({
        color: 'error',
        message: 'Por favor, informe as informações do chamado',
      });
      return;
    }

    const observationValue = this.formGroup.get('observation')?.value || '';
    this.newDetail!.observation = observationValue;
    this.newDetail!.pub = this.formGroup.get('isPublic')!.value;
    this._sendDetail();
  }

  async _sendDetail() {
    this.newDetail!.user = this.user;
    this.newDetail!.user_id = this.user?.id;
    this.newDetail!.active = true;

    this.saving.emit(true);
    let response = await this.ticketService.addComment(
      this.unconfirmedTicket!.id!,
      this.newDetail!
    );

    if (response?.id) {
      this.saving.emit(false);
      this.formGroup.controls['observation'].patchValue('');
      this.formGroup.markAsPristine();
      this.resetInfoTextareaRows();
      this.dataService!.assembleTicketEmitter!.emit(response!);
      this.loadTicketMovementHistories();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => {
      s.unsubscribe();
    });
  }

  getTicketForm(): FormGroup {
    return this.formGroup;
  }

  getTimelineIcon(detail: any) {
    if (detail.type === 'MH' || detail.type === 'DMH') {
      return 'bi-clock-history';
    } else if (detail.startDate) {
      return 'bi-clock-fill';
    } else if (detail.pub) {
      return 'bi-globe-americas';
    } else {
      return 'bi-lock-fill';
    }
  }

  onInfoTextareaFocus(): void {
    this.infoTextareaRows = this.infoTextareaFocusedRows;
  }

  onInfoTextareaBlur(): void {
    setTimeout(() => {
      this.resetInfoTextareaRows();
      this.cdr.markForCheck();
    }, 200);
  }

  private resetInfoTextareaRows(): void {
    this.infoTextareaRows = this.infoTextareaBaseRows;
  }
}
